import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { X, Smartphone, Copy, Check, ShieldCheck } from 'lucide-react';
import { getAppInviteUrl } from '../utils/urlHelper';

interface QrModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
  accessCode: string;
}

export const QrModal: React.FC<QrModalProps> = ({ isOpen, onClose, roomId, accessCode }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [copied, setCopied] = useState(false);

  const inviteUrl = getAppInviteUrl(roomId, accessCode);

  useEffect(() => {
    if (isOpen && canvasRef.current && inviteUrl) {
      QRCode.toCanvas(
        canvasRef.current,
        inviteUrl,
        {
          width: 260,
          margin: 2,
          color: {
            dark: '#00F0FF',
            light: '#07080E'
          }
        },
        (error) => {
          if (error) console.error('QR code error:', error);
        }
      );
    }
  }, [isOpen, inviteUrl]);

  if (!isOpen) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative glass-card w-full max-w-sm p-6 text-center shadow-2xl border border-cyan-500/30">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-xl hover:bg-white/5 transition-colors"
        >
          <X size={18} />
        </button>

        {/* Icon & Title */}
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-purple-600 p-[2px] mx-auto mb-3 shadow-glow-cyan">
          <div className="w-full h-full bg-[#090A12] rounded-[14px] flex items-center justify-center text-cyan-400">
            <Smartphone size={26} />
          </div>
        </div>

        <h2 className="text-xl font-bold text-white font-display mb-1">Instant Device Pairing</h2>
        <p className="text-xs text-slate-400 mb-5 max-w-xs mx-auto">
          Scan with your Android camera or iPhone to link with room{' '}
          <span className="text-cyan-300 font-mono font-bold">{roomId}</span> seamlessly.
        </p>

        {/* QR Code Canvas */}
        <div className="p-3 bg-[#07080E] rounded-2xl border border-cyan-500/30 inline-block shadow-2xl mb-4 relative">
          <canvas ref={canvasRef} className="rounded-xl mx-auto" />
        </div>

        {/* Room Info */}
        <div className="bg-black/50 rounded-xl p-3 border border-white/5 text-xs mb-4 text-left font-mono space-y-1">
          <div className="flex justify-between">
            <span className="text-slate-400 font-sans">Room Identifier:</span>
            <span className="text-white font-bold">{roomId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400 font-sans">Encryption Key:</span>
            <span className="text-cyan-400 font-bold">{accessCode}</span>
          </div>
        </div>

        {/* Action Button */}
        <button onClick={handleCopyLink} className="btn-primary w-full py-3 text-xs font-bold shadow-lg shadow-cyan-500/25">
          {copied ? <Check size={15} /> : <Copy size={15} />}
          <span>{copied ? 'Direct Link Copied!' : 'Copy Direct Pairing Link'}</span>
        </button>

        <div className="mt-3.5 flex items-center justify-center gap-1.5 text-[11px] text-slate-500 font-medium">
          <ShieldCheck size={13} className="text-cyan-400" />
          <span>256-bit AES-GCM Cryptographic Mesh</span>
        </div>
      </div>
    </div>
  );
};
